import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { VendingPackageTimersDurationAuditPendingByProductListChart, takeRankedChartSeries, toChartListItems } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';

/** Ranked preview size on the insights overview card. */
const OVERVIEW_PRODUCT_LIMIT = 10;

export interface VendingLongestReviewWaitsItem {
    productId: string;
    /** Open package_audit_pending timer duration in seconds. */
    durationSeconds: number;
}

const isDisplayableLabel = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 && normalized !== 'none' && normalized !== 'default';
};

const toProductItems = (chart: Chart): VendingLongestReviewWaitsItem[] =>
    toChartListItems(chart, '')
        .filter((item) => isDisplayableLabel(item.key) && item.value > 0)
        .map((item) => ({
            productId: item.key,
            durationSeconds: item.value
        }));

export const useVendingLongestReviewWaitsList = (accountId: string) => {
    const isDetailMode = useInsightDetailMode();
    const [items, setItems] = useState<VendingLongestReviewWaitsItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(accountId, VendingPackageTimersDurationAuditPendingByProductListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [DefaultQueryParams.SORTDIRECTION]: 'desc'
        })
            .then((chart) => {
                if (cancelled) return;
                const ranked = isDetailMode
                    ? takeRankedChartSeries(chart, 'highest', Number.POSITIVE_INFINITY)
                    : takeRankedChartSeries(chart, 'highest', OVERVIEW_PRODUCT_LIMIT);
                setItems(toProductItems(ranked));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, fetchRange.key, isDetailMode]);

    return { items, isLoading, hasData: items.length > 0, isDetailMode };
};
